import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import { getApiUrl, getAuthHeaders } from '../../config/api'
import toast from 'react-hot-toast'
import { EyeIcon, EyeSlashIcon, UserIcon, LockClosedIcon, ArrowRightIcon, ArrowLeftIcon } from '@heroicons/react/24/outline'

const REMEMBER_ME_KEY = 'philiapedia_remember_email'

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [focusedField, setFocusedField] = useState<string>('')
  const [error, setError] = useState<string>('')
  const [rememberMe, setRememberMe] = useState(false)
  
  const navigate = useNavigate()
  const setUser = useAuthStore(state => state.setUser)
  const setTokens = useAuthStore(state => state.setTokens)

  // 加载记住的邮箱
  useEffect(() => {
    const savedEmail = localStorage.getItem(REMEMBER_ME_KEY)
    if (savedEmail) {
      setFormData(prev => ({ ...prev, email: savedEmail }))
      setRememberMe(true)
    }
  }, [])

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.email.trim()) {
      newErrors.email = '请输入邮箱地址'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = '请输入有效的邮箱地址'
    }
    
    if (!formData.password) {
      newErrors.password = '请输入密码'
    } else if (formData.password.length < 6) {
      newErrors.password = '密码长度不能少于6位'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      // 验证输入
      if (!formData.email || !formData.password) {
        setError('请填写所有必填字段')
        return
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        setError('请输入有效的邮箱地址')
        return
      }

      // 使用配置的API URL而不是硬编码
      const response = await fetch(getApiUrl('/auth/login'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData),
        credentials: 'include'
      })

      const data = await response.json()

      if (response.ok) {
        // 保存用户信息到状态管理
        setUser(data.user)
        setTokens(data.token, data.refreshToken || data.token)
        
        // 保存token到localStorage
        localStorage.setItem('token', data.token)
        
        // 处理"记住我"功能
        if (rememberMe) {
          localStorage.setItem(REMEMBER_ME_KEY, formData.email)
        } else {
          localStorage.removeItem(REMEMBER_ME_KEY)
        }
        
        toast.success('登录成功！正在跳转...', {
          icon: '🎉',
          duration: 2000,
        })
        
        // 根据用户角色跳转到不同页面
        setTimeout(() => {
          if (data.user.role === 'admin') {
            navigate('/admin/dashboard')
          } else if (data.user.role === 'teacher') {
            // 对于老师用户，先检查审核状态
            checkTeacherStatusAndRedirect(data.token)
          } else {
            navigate('/student/dashboard')
          }
        }, 1000)
      } else {
        toast.error(data.message || '登录失败，请检查邮箱和密码')
      }
    } catch (error) {
      toast.error('网络连接失败，请稍后重试')
    } finally {
      setIsLoading(false)
    }
  }

  const checkTeacherStatusAndRedirect = async (token: string) => {
    try {
      const response = await fetch('/api/teachers/application-status', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        if (data.status === 'active') {
          // 审核通过，进入仪表板
          navigate('/teacher/dashboard')
        } else {
          // 未通过审核，进入申请状态页面
          navigate('/teacher/application-status')
        }
      } else {
        // 没有找到申请记录，可能是新用户
        navigate('/teacher/application-status')
      }
    } catch (error) {
      console.error('检查教师状态失败:', error)
      navigate('/teacher/application-status')
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value
    })
    // 清除对应字段的错误
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      })
    }
  }

  const handleFocus = (fieldName: string) => {
    setFocusedField(fieldName)
  }

  const handleBlur = () => {
    setFocusedField('')
  }

  const loginHighlights = [
    { title: 'AI 伴学', desc: '自动记录学习数据与提醒' },
    { title: '多端同步', desc: '手机 / 网页 / iPad 即时同步' },
    { title: '安全加密', desc: '企业级加密守护学习资料' }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 py-6 sm:py-12 px-3 sm:px-6 lg:px-8">
      {/* 返回首页按钮 */}
      <Link
        to="/"
        className="fixed top-3 sm:top-6 left-3 sm:left-6 z-20 flex items-center space-x-1.5 sm:space-x-2 bg-white/80 backdrop-blur-lg rounded-lg sm:rounded-xl px-3 sm:px-4 py-1.5 sm:py-2 shadow-lg hover:shadow-xl transition-all duration-300 hover:bg-white/90 text-gray-700 hover:text-blue-600"
      >
        <ArrowLeftIcon className="h-4 w-4 sm:h-5 sm:w-5" />
        <span className="font-medium text-sm sm:text-base">返回首页</span>
      </Link>

      {/* 背景装饰 - 移动端简化 */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-48 sm:w-96 h-48 sm:h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="hidden sm:block absolute top-0 right-0 w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="hidden sm:block absolute -bottom-8 left-20 w-96 h-96 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto grid lg:grid-cols-2 gap-6 sm:gap-10 items-center pt-10 sm:pt-0">
        <div className="hidden lg:flex flex-col text-gray-900">
          <div className="bg-white/80 backdrop-blur-2xl rounded-3xl border border-white/70 shadow-2xl p-10">
            <p className="text-xs uppercase tracking-[0.4em] text-blue-400 mb-4">philiapedia</p>
            <h2 className="text-4xl font-bold leading-tight mb-4">登录后，继续你的全球课堂</h2>
            <p className="text-gray-600 mb-8">
              AI 会自动同步你的课程进度、项目任务与反馈。每一次登录，都能看到最重要的下一步行动。
            </p>
            <div className="space-y-5">
              {loginHighlights.map(item => (
                <div key={item.title} className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-500 flex items-center justify-center font-semibold">
                    ✓
                  </div>
                  <div>
                    <p className="text-lg font-semibold">{item.title}</p>
                    <p className="text-gray-500 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      <div className="relative z-10 max-w-md w-full mx-auto lg:mx-0">
        {/* 登录卡片 */}
        <div className="bg-white/90 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl border border-gray-100 p-6 sm:p-10 animate-fadeInUp">
          <div className="text-center mb-6 sm:mb-8">
            {/* Logo区域 */}
            <div className="mx-auto w-14 h-14 sm:w-18 sm:h-18 bg-gradient-to-br from-blue-500 via-indigo-600 to-purple-600 rounded-2xl sm:rounded-3xl flex items-center justify-center mb-4 sm:mb-6 shadow-xl shadow-blue-500/30">
              <UserIcon className="h-7 w-7 sm:h-9 sm:w-9 text-white" />
            </div>
            
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">
              欢迎回来
            </h2>
            <p className="text-sm sm:text-base text-gray-600">
              登录您的账户，继续学习之旅
            </p>
          </div>

          {/* 快捷登录方式 */}
          <div className="mb-6">
            <div className="flex items-center space-x-4 text-sm">
              <Link 
                to="/phone-login" 
                className="flex-1 text-center py-2 px-4 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg transition-colors duration-200 hover:scale-105 transform"
              >
                📱 手机号登录
              </Link>
              <Link 
                to="/register" 
                className="flex-1 text-center py-2 px-4 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors duration-200 hover:scale-105 transform"
              >
                🆕 立即注册
              </Link>
            </div>
          </div>

          {/* 分割线 */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">或使用邮箱登录</span>
            </div>
          </div>
          
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* 邮箱输入框 */}
            <div className="space-y-1">
              <label htmlFor="email" className="text-sm font-medium text-gray-700 block">
                邮箱地址
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <UserIcon className={`h-5 w-5 transition-colors duration-200 ${
                    focusedField === 'email' ? 'text-blue-500' : 'text-gray-400'
                  }`} />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className={`block w-full pl-10 pr-3 py-3 border rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                    errors.email 
                      ? 'border-red-300 bg-red-50' 
                      : focusedField === 'email'
                        ? 'border-blue-300 bg-blue-50/50'
                        : 'border-gray-300 bg-gray-50/50 hover:bg-white'
                  }`}
                  placeholder="请输入您的邮箱"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={() => handleFocus('email')}
                  onBlur={handleBlur}
                />
              </div>
              {errors.email && (
                <p className="text-sm text-red-600 animate-slideUp">{errors.email}</p>
              )}
            </div>

            {/* 密码输入框 */}
            <div className="space-y-1">
              <label htmlFor="password" className="text-sm font-medium text-gray-700 block">
                密码
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <LockClosedIcon className={`h-5 w-5 transition-colors duration-200 ${
                    focusedField === 'password' ? 'text-blue-500' : 'text-gray-400'
                  }`} />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className={`block w-full pl-10 pr-12 py-3 border rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                    errors.password 
                      ? 'border-red-300 bg-red-50' 
                      : focusedField === 'password'
                        ? 'border-blue-300 bg-blue-50/50'
                        : 'border-gray-300 bg-gray-50/50 hover:bg-white'
                  }`}
                  placeholder="请输入您的密码"
                  value={formData.password}
                  onChange={handleChange}
                  onFocus={() => handleFocus('password')}
                  onBlur={handleBlur}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-gray-100 rounded-r-xl transition-colors duration-200"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-sm text-red-600 animate-slideUp">{errors.password}</p>
              )}
            </div>

            {/* 记住我和忘记密码 */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                  记住我
                </label>
              </div>

              <div className="text-sm">
                <Link
                  to="/forgot-password"
                  className="text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200"
                >
                  忘记密码？
                </Link>
              </div>
            </div>

            {/* 登录按钮 */}
            <button
              type="submit"
              disabled={isLoading}
              className={`group relative w-full flex justify-center py-3.5 px-4 border border-transparent text-sm font-semibold rounded-2xl text-white transition-all duration-300 ${
                isLoading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:shadow-xl hover:shadow-blue-500/25 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transform hover:-translate-y-0.5 shadow-lg'
              }`}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  登录中...
                </div>
              ) : (
                <div className="flex items-center">
                  登录
                  <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                </div>
              )}
            </button>
          </form>

          {/* 注册链接 */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600">
              还没有账户？{' '}
              <Link 
                to="/register" 
                className="font-medium text-blue-600 hover:text-blue-700 transition-colors duration-200"
              >
                立即注册，开始学习
              </Link>
            </p>
          </div>

          {/* 特色说明 */}
          <div className="mt-8 grid grid-cols-3 gap-4 text-center">
            <div className="text-center">
              <div className="text-2xl mb-1">🚀</div>
              <div className="text-xs text-gray-600">快速登录</div>
            </div>
            <div className="text-center">
              <div className="text-2xl mb-1">🔒</div>
              <div className="text-xs text-gray-600">安全可靠</div>
            </div>
            <div className="text-center">
              <div className="text-2xl mb-1">📚</div>
              <div className="text-xs text-gray-600">海量课程</div>
            </div>
          </div>
        </div>

        {/* 底部链接 */}
        <div className="mt-6 text-center">
          <div className="flex items-center justify-center space-x-6 text-sm text-gray-500">
            <Link to="/privacy" className="hover:text-gray-700 transition-colors">隐私政策</Link>
            <span>|</span>
            <Link to="/terms" className="hover:text-gray-700 transition-colors">服务条款</Link>
            <span>|</span>
            <Link to="/help" className="hover:text-gray-700 transition-colors">帮助中心</Link>
          </div>
        </div>
      </div>
    </div>
  </div>
  )
}

export default LoginPage 